import { gateway } from '../../../config/serviceUrls';
import { fallbackPrimary, readCachedSchoolBranding } from '../../../utils/brandTheme';
import type { TutorTenantLanguageHint } from '../../tutor/tutorTenantLabels';

export interface EffectiveBranding {
  logo_url: string;
  display_name: string;
  display_name_ta: string;
  theme_color: string;
  language?: TutorTenantLanguageHint | null;
}

export interface SchoolBrandingDisplay {
  logoUrl: string;
  displayName: string;
  displayNameTa: string;
  themeColor: string;
  language?: TutorTenantLanguageHint | null;
}

/** Mirrors platform-service DEFAULT_LOGO_URL — used only when branding API is unavailable. */
export const DEFAULT_PLATFORM_LOGO_URL = '/images/tkm-logo.png';

/**
 * Turn API logo paths into browser-loadable URLs.
 * Uploaded school logos are stored as `/static/schools/...` on the gateway, not the Vite dev server.
 */
export function resolveBrandingAssetUrl(url: string | null | undefined): string {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/')) {
    const base = gateway.replace(/\/$/, '');
    return `${base}${trimmed}`;
  }
  return trimmed;
}

export function mapEffectiveBranding(data: EffectiveBranding): SchoolBrandingDisplay {
  return {
    logoUrl: resolveBrandingAssetUrl(data.logo_url),
    displayName: data.display_name,
    displayNameTa: data.display_name_ta,
    themeColor: data.theme_color,
    language: data.language,
  };
}

/**
 * Last-resort branding when the API cannot be reached.
 * Prefers cached effective branding from a prior successful load.
 */
export function platformBrandingFallback(): EffectiveBranding {
  const cached = readCachedSchoolBranding();
  if (cached?.displayName && cached.displayNameTa && cached.logoUrl) {
    return {
      logo_url: cached.logoUrl,
      display_name: cached.displayName,
      display_name_ta: cached.displayNameTa,
      theme_color: cached.themeColor,
    };
  }
  return {
    logo_url: DEFAULT_PLATFORM_LOGO_URL,
    display_name: 'Tamil Katral Maiyam',
    display_name_ta: 'தமிழ் கற்றல் மையம்',
    theme_color: fallbackPrimary(),
  };
}
